/**
 * One month's payout statement: what was collected, what CoLiviq charged, and
 * what was transferred (COL-528).
 *
 * Three lines and a total, in that order, because that is the arithmetic an
 * owner is checking — and every one of them is READ from the row the payout
 * confirmation recorded. The prototype computed the fee as 15 % of the rent in
 * the browser; a statement that recomputes its own figures can disagree with
 * the money that actually moved, and with the PDF underneath it.
 */
import { Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import type { OwnerPayoutStatement } from "@/hooks/owner/useOwnerPayoutStatements";
import { formatEuroCents } from "@/lib/owner-money";
import { explainFee, formatRateCents } from "@/lib/owner-payout-fee";
import { useDownloadPayoutStatement } from "@/hooks/owner/useDownloadPayoutStatement";

const C = {
  petrol: "#0F4C4A",
  hairline: "#ECE7DD",
  muted: "#7A8E8C",
  body: "#47605E",
  clay: "#C0714F",
} as const;

export function OwnerPayoutStatementDialog({
  statement,
  onOpenChange,
}: {
  statement: OwnerPayoutStatement | null;
  onOpenChange: (next: boolean) => void;
}) {
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-DE" : "en-GB";
  const download = useDownloadPayoutStatement();

  if (!statement) return null;

  const eur = (cents: number) => formatEuroCents(cents, locale);
  const fee = explainFee(statement);
  const monthLabel = new Date(
    `${statement.period_month}T00:00:00`
  ).toLocaleDateString(locale, { month: "long", year: "numeric" });
  const transferred = statement.paid_at
    ? new Date(statement.paid_at).toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  // What the fee was charged ON comes from the invoice's own snapshot, never
  // from dividing the fee by the rent: a flat per-room fee is not a share of
  // the rent, and a statement that says otherwise is wrong in the owner's books.
  const feeLabel =
    fee.kind === "waived"
      ? // A referral free month is "gebührenfrei", not "0 % der Nettokaltmiete"
        // (Neel, owner-portal thread).
        t("owner.payouts.feeWaived", "coliviq-Gebühr (gebührenfrei)")
      : fee.kind === "pct"
        ? t(
            "owner.payouts.feeLine",
            "coliviq-Gebühr ({pct} % der Nettokaltmiete)"
          ).replace(
            "{pct}",
            String(fee.pct).replace(".", language === "de" ? "," : ".")
          )
        : t("owner.payouts.feeLabel", "coliviq-Verwaltungsgebühr");

  const feeBasis =
    fee.kind === "flat"
      ? fee.groups
          .map((g) =>
            t(
              g.rooms === 1
                ? "owner.payouts.feeFlatOne"
                : "owner.payouts.feeFlat",
              // "1 Zimmer" and "3 Zimmer" read the same in German; English
              // needs its own singular, so the two keys exist.
              "{n} Zimmer × {rate} pro Zimmer"
            )
              .replace("{n}", String(g.rooms))
              .replace("{rate}", formatRateCents(g.rateCents, locale))
          )
          .join(" + ")
      : null;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px] rounded-[18px]">
        <DialogHeader>
          <p
            className="text-[11px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: C.muted }}
          >
            {t("owner.payouts.statement", "Gutschrift")}
            {statement.invoice_no ? ` · ${statement.invoice_no}` : ""}
          </p>
          <DialogTitle className="text-[22px]" style={{ color: C.petrol }}>
            {monthLabel}
          </DialogTitle>
          <DialogDescription style={{ color: C.muted }}>
            {statement.property_title ?? ""}
          </DialogDescription>
        </DialogHeader>

        <dl className="mt-1">
          <div
            className="flex items-center justify-between border-b py-3.5"
            style={{ borderColor: C.hairline }}
          >
            <dt className="text-[13.5px]" style={{ color: C.body }}>
              {t("owner.payouts.rentCollected", "Eingezogene Miete")}
            </dt>
            <dd
              className="text-[13.5px] tabular-nums"
              style={{ color: C.petrol }}
            >
              {eur(statement.rent_collected_cents)}
            </dd>
          </div>

          <div
            className="flex items-start justify-between border-b py-3.5"
            style={{ borderColor: C.hairline }}
          >
            <dt className="text-[13.5px]" style={{ color: C.body }}>
              {feeLabel}
              {feeBasis && (
                <span
                  className="mt-0.5 block text-xs"
                  style={{ color: C.muted }}
                >
                  {feeBasis}
                </span>
              )}
            </dt>
            <dd
              className="text-[13.5px] tabular-nums"
              style={{ color: C.clay }}
            >
              {/* A deduction, so it reads as one. A zero fee is still printed
                  rather than hidden: the line is what explains the total. */}
              {statement.fee_cents > 0 ? "− " : ""}
              {eur(statement.fee_cents)}
            </dd>
          </div>

          <div className="flex items-baseline justify-between py-4">
            <dt
              className="text-[15px] font-semibold"
              style={{ color: C.petrol }}
            >
              {t("owner.payouts.netPayout", "Netto-Auszahlung")}
            </dt>
            <dd
              className="text-[17px] font-bold tabular-nums"
              style={{ color: C.petrol }}
            >
              {eur(statement.net_payout_cents)}
            </dd>
          </div>
          {transferred && (
            <p className="-mt-2 text-xs" style={{ color: C.muted }}>
              {t("owner.payouts.transferredOn", "Überwiesen am {date}").replace(
                "{date}",
                transferred
              )}
            </p>
          )}
        </dl>

        <div className="mt-2">
          <button
            type="button"
            onClick={() => download.mutate(statement.invoice_id)}
            disabled={download.isPending}
            className="inline-flex h-10 items-center gap-2 rounded-full px-4 text-[13px] font-semibold text-white disabled:opacity-60"
            style={{ background: C.petrol }}
          >
            <Download className="h-4 w-4" />
            {download.isPending
              ? t("owner.payouts.preparing", "Wird erstellt…")
              : t("owner.payouts.downloadPdf", "Herunterladen (PDF)")}
          </button>
          {download.isError && (
            <p className="mt-2 text-xs" style={{ color: C.clay }} role="alert">
              {t(
                "owner.payouts.downloadFailed",
                "Die Gutschrift konnte nicht erstellt werden. Bitte versuchen Sie es erneut."
              )}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
